import moment from "moment";
import { formatCurrency } from "../Billing/billingOverview.utils";
import {
  buildBillingPath,
  buildDashboardFinancialLinks,
  buildDashboardPlatformLinks,
  buildStripePath,
} from "../platformAdminNavigation.utils";

export const formatMetricValue = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }

  return String(value);
};

export const getGreetingName = (user) => {
  if (!user) {
    return "there";
  }

  if (user.first_name) {
    return user.first_name;
  }

  if (user.name) {
    return user.name.split(" ")[0];
  }

  if (user.username) {
    return user.username.split(" ")[0];
  }

  if (user.email) {
    return user.email.split("@")[0];
  }

  return "there";
};

export const formatRecentActivityPrimary = (activity) => {
  if (activity?.description) {
    return activity.description;
  }

  if (activity?.action) {
    return activity.action.replace(/\./g, " · ").replace(/_/g, " ");
  }

  return "Platform activity";
};

export const formatRecentActivitySecondary = (activity) => {
  const admin = activity?.admin?.name || activity?.admin?.email;
  const tenant = activity?.tenant?.sp_name;

  if (admin && tenant) {
    return `${admin} · ${tenant}`;
  }

  return admin || tenant || "—";
};

export const getDashboardSubtitle = (computedAt) => {
  if (!computedAt) {
    return "Platform overview";
  }

  const updated = moment(computedAt);
  if (!updated.isValid()) {
    return "Platform overview";
  }

  return `Platform overview · Updated ${updated.fromNow()}`;
};

export const buildNeedsAttentionItems = (
  trialsExpiring = [],
  failedPayments = [],
  { includeTrials = true, includeFailedPayments = true } = {}
) => {
  const items = [];

  if (includeTrials) {
    trialsExpiring.forEach((tenant) => {
      items.push({
        id: `trial-${tenant.id}`,
        tenantId: tenant.id,
        label: `Trial expiring — ${tenant.name}`,
        detail: tenant.trial_end_date
          ? `Ends ${moment(tenant.trial_end_date).format("D MMM YYYY")}`
          : null,
        borderClass: "border-amber-400",
      });
    });
  }

  if (includeFailedPayments) {
    failedPayments.forEach((tenant) => {
      const statusLabel = tenant.subscription_current_status
        ? tenant.subscription_current_status.replace(/_/g, " ")
        : "failed";

      items.push({
        id: `payment-${tenant.id}`,
        tenantId: tenant.id,
        label: `Payment failed — ${tenant.name}`,
        detail: statusLabel,
        borderClass: "border-red-400",
      });
    });
  }

  return items;
};

export const buildRecentSignupItems = (recentSignups = []) =>
  recentSignups.map((tenant) => ({
    id: tenant.id,
    name: tenant.name,
    email: tenant.email,
    createdAt: tenant.created_at,
  }));

export const buildDashboardWidgetSections = ({
  stats = {},
  billingPayload = null,
  capabilities = {},
  canViewProspects: legacyCanViewProspects,
}) => {
  const canViewProspects =
    capabilities.canViewProspects ?? legacyCanViewProspects ?? false;
  const canListTenants = capabilities.canListTenants ?? true;
  const canViewBilling = capabilities.canViewBilling ?? Boolean(billingPayload);

  const platform = [];

  if (canListTenants) {
    const platformLinks = buildDashboardPlatformLinks({ canViewProspects });

    platform.push(
      {
        id: "total_tenants",
        label: "Tenants",
        value: formatMetricValue(stats.total_tenants),
        to: platformLinks.total_tenants,
        title: "All tenants on the platform",
      },
      {
        id: "active_tenants",
        label: "Active",
        value: formatMetricValue(stats.active_tenants ?? stats.total_tenants),
        to: platformLinks.active_tenants,
        title: "Active service providers",
      },
      {
        id: "on_trial",
        label: "On trial",
        value: formatMetricValue(stats.on_trial),
        to: platformLinks.on_trial,
        title: "Tenants currently on trial",
      },
      {
        id: "suspended",
        label: "Suspended",
        value: formatMetricValue(stats.suspended),
        to: platformLinks.suspended,
        variant: Number(stats.suspended) > 0 ? "attention" : "default",
        title: "Suspended tenants",
      },
      {
        id: "read_only",
        label: "Read-only",
        value: formatMetricValue(stats.read_only),
        to: platformLinks.read_only,
        title: "Tenants in read-only mode",
      }
    );
  }

  if (canViewProspects) {
    const platformLinks = buildDashboardPlatformLinks({ canViewProspects: true });
    platform.push({
      id: "prospects",
      label: "Prospects",
      value: formatMetricValue(stats.prospect_count),
      to: platformLinks.prospects,
      title: "Sales prospects",
    });
  }

  if (!canViewBilling || !billingPayload) {
    return { platform, financial: [] };
  }

  const billingStats = billingPayload.stats || {};
  const revenue = billingPayload.revenue_summary || {};
  const estimatedMrr = billingPayload.estimated_mrr || {};
  const failedPayments = Number(billingStats.failed_payments) || 0;
  const openInvoices = Number(revenue.open_invoice_count) || 0;
  const financialLinks = buildDashboardFinancialLinks(billingStats, revenue);

  const financial = [
    {
      id: "paying_tenants",
      label: "Paying (Stripe)",
      value: formatMetricValue(billingStats.active_subscriptions),
      to: financialLinks.paying_tenants,
      variant: "success",
      title: "Tenants with active Stripe subscriptions",
    },
    {
      id: "estimated_mrr",
      label: "Est. MRR",
      value: estimatedMrr.available
        ? formatCurrency(estimatedMrr.amount_cents, estimatedMrr.currency)
        : "—",
      to: financialLinks.estimated_mrr,
      hint: estimatedMrr.available ? "Stripe catalog" : "Unavailable",
      title: "Estimated monthly recurring revenue",
    },
    {
      id: "collected_30d",
      label: "Collected 30d",
      value: formatCurrency(revenue.collected_last_30_days_cents, revenue.currency),
      to: financialLinks.collected_30d,
      title: "Stripe invoices collected in the last 30 days",
    },
    {
      id: "collected_all_time",
      label: "Collected (all)",
      value: formatCurrency(revenue.collected_all_time_cents, revenue.currency),
      to: financialLinks.collected_all_time,
      title: "Total collected from synced Stripe invoices",
    },
    {
      id: "outstanding",
      label: "Outstanding",
      value: revenue.outstanding_cents
        ? formatCurrency(revenue.outstanding_cents, revenue.currency)
        : "$0",
      to: financialLinks.outstanding,
      hint: openInvoices ? `${openInvoices} open` : null,
      variant: openInvoices > 0 ? "attention" : "default",
      title: "Open Stripe invoice balance",
    },
    {
      id: "failed_payments",
      label: "Failed payments",
      value: formatMetricValue(billingStats.failed_payments),
      to: financialLinks.failed_payments,
      variant: failedPayments > 0 ? "danger" : "default",
      title: "Tenants with failed Stripe subscription status",
    },
  ];

  return { platform, financial };
};
